import { Link } from 'react-router-dom';
// RefreshCw was the icon for the cut "Regular Audits" pillar. Dropped with it.
import { Shield, Lock, Server, Eye, Mail } from 'lucide-react';
import SEO from '@/components/SEO';
import '@/styles/article.css';
import '@/styles/landing.css';

const pillar = (icon, title, body) => ({ icon, title, body });

// Rewritten 2026-08-17. Every claim on this page is now checked against the code
// that implements it. What was here before was not: it claimed AES-256 at rest
// (MongoDB Community has no encryption at rest), object storage (screenshots are
// base64 inside the audit documents), a 99.9% uptime SLA, automatic failover, and
// continuous 24/7 monitoring (there is no monitoring infrastructure in the repo at
// all), and that all staff access is logged (no such log exists).
//
// This is the page a buyer reads before uploading a screenshot of their internal
// dashboard. Do not add a claim here without pointing at the code that makes it
// true. If a control is desirable but absent, build it — do not describe it.
const pillars = [
  // Amendment B: no claim about disk encryption in EITHER direction. Implying the
  // provider encrypts the volume would be asserting something unverified; the VPS
  // disk state is being checked separately. State only what we know.
  pillar(Shield, 'Data Encryption', 'Data in transit is encrypted using TLS 1.2+. We do not currently operate database-level encryption at rest.'),
  pillar(Lock, 'Access Control', 'Your data is isolated per account, enforced on the server for every read rather than hidden in the browser. Administrative access is limited to the company owner.'),
  pillar(Server, 'Infrastructure', 'We run on a dedicated cloud server behind a TLS-terminating reverse proxy, with the database on a persistent volume. We do not offer a contractual uptime SLA.'),
  pillar(Eye, 'Monitoring', 'Audit pipeline failures are recorded and alert us by email, including a distinct alert when failures cluster in a short window. We do not operate continuous 24/7 infrastructure monitoring.'),
  // The "Regular Audits" pillar was cut: we cannot point to any security review.
  pillar(Mail, 'Responsible Disclosure', 'Found a vulnerability? We welcome responsible disclosure. Contact us at security@uxauditpro.com and we will respond within 48 hours.'),
];

// A fragment, not a wrapper. article.css binds the measure and the paragraph
// rhythm with CHILD selectors (`.uxa-article > p`), so the h2 and the prose have
// to be direct children of the article. Wrapping them in two divs is what kept
// these pages at 14px while the blog moved to 19.
const Section = ({ title, children }) => (
  <>
    <h2>{title}</h2>
    {children}
  </>
);

export default function SecurityPage() {
  return (
    <div className="min-h-screen pt-20">
      <SEO
        title="Security Policy: How We Protect Your Data"
        description="How UXAuditPro protects your data: TLS 1.2+ in transit, per-account isolation enforced server-side, no third-party file storage, and responsible disclosure."
        canonical="/security"
      />
      {/* THE MEASURE STAYS AT max-w-3xl. These are article pages; article.css
          binds --article-measure to 68ch and the module was verified conformant
          on 2026-08-19. The brand shell goes around the column, never through
          it. The grid wash sits behind the document header only. */}
      <div className="max-w-3xl mx-auto px-6 py-16" style={{ position: 'relative' }}>
        {/* Header */}
        <div className="mb-12" style={{ position: 'relative' }}>
          <div className="uxa-hero-grid" aria-hidden="true" /* inset was '-64px -40px 0' and produced 16px of horizontal
                   overflow at 390 and 768 — the column is px-6 (24px), so a
                   -40px bleed has nowhere to go. Measured on the built page:
                   scrollWidth 406 against a 390 client. A decorative wash is
                   never a reason for the page to scroll sideways. */
                style={{ inset: '-64px 0 0' }} />
          <div style={{ position: 'relative' }}>
          <span className="text-sm font-bold uppercase tracking-widest" style={{ color: 'var(--accent-text)' }}>Trust & Safety</span>
          <h1 className="uxa-h1 mt-3" style={{ color: 'var(--ink-1)', textWrap: 'pretty' }}>
            Security
          </h1>
          <p className="mt-4 text-base" style={{ color: 'var(--ink-2)' }}>
            We take the security of your data seriously. Here's how we protect it.
          </p>
          </div>
        </div>

        {/* Pillars grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12">
          {pillars.map(({ icon: Icon, title, body }) => (
            <div key={title} className="uxa-feature-card" style={{ background: 'var(--surface-white)', padding: 'var(--space-6)' }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: 'var(--accent-tint)' }}>
                <Icon className="w-5 h-5" style={{ color: 'var(--accent-text)' }} />
              </div>
              <h3 className="uxa-h3 mb-2" style={{ color: 'var(--ink-1)' }}>{title}</h3>
              <p className="uxa-dense" style={{ color: 'var(--ink-2)' }}>{body}</p>
            </div>
          ))}
        </div>

        {/* Details */}
        <div className="uxa-article uxa-feature-card" style={{ background: 'var(--surface-white)', padding: 'var(--space-10)' }}>
          <Section title="Authentication">
            <p>We support Google OAuth for secure, passwordless sign-in. We never store plaintext passwords. Session tokens are HTTP-only cookies with short expiration windows and are invalidated on logout.</p>
          </Section>

          <Section title="Uploaded Files">
            <p>Uploaded files and screenshots are stored inside your audit record in our database, not with a third-party storage provider, and are never shared with other users or third parties. They are read by our analysis pipeline and by you.</p>
            {/* This sentence was held back until it was true. It says images are cleared
                after 45 days, which requires the purge to actually clear ALL of them —
                and until 90aae92 the job cleared `screenshots` while seven other
                locations on the same document kept the bytes. That branch is merged and
                verified live (audit/screenshot_retention.py, tests/track7).

                If the purge regresses, this sentence becomes false. It is a claim about
                a running job, so it is only as true as the job. */}
            <p>Screenshots captured or uploaded for an audit are automatically cleared from the audit record 45 days after it is created, by a job that runs daily. You can request earlier deletion by contacting support.</p>
          </Section>

          <Section title="Payment Security">
            <p>We do not store credit card or payment details on our servers. All payments are processed through PayPal's PCI-compliant infrastructure. We only store transaction IDs and plan status.</p>
          </Section>

          <Section title="Reporting a Vulnerability">
            <p>If you discover a security issue, please report it to us privately at <a href="mailto:security@uxauditpro.com">security@uxauditpro.com</a> before public disclosure. Please include a description of the vulnerability and steps to reproduce.</p>
            <p>We commit to acknowledging your report within 48 hours and resolving confirmed critical issues within 7 days. We do not pursue legal action against researchers acting in good faith.</p>
          </Section>
        </div>

        {/* 44px MINIMUM. These measured 88x20, 53x20 and 34x20 on the built
            page at all four widths. They are a standalone document-switcher
            row, not links inside a sentence, so WCAG 2.5.8's inline-prose
            exemption does not reach them. Presented as a 44px hit area via
            inline-flex so the row does not move. */}
        <div className="mt-8 flex gap-4 text-sm items-center" style={{ color: 'var(--ink-2)' }}>
          <Link to="/privacy/" style={{ color: 'var(--accent-text)', display: 'inline-flex', alignItems: 'center', minHeight: 44 }}>Privacy Policy</Link>
          <span>·</span>
          <Link to="/terms/" style={{ color: 'var(--accent-text)', display: 'inline-flex', alignItems: 'center', minHeight: 44 }}>Terms of Service</Link>
          <span>·</span>
          <Link to="/legal/" style={{ color: 'var(--accent-text)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minHeight: 44, minWidth: 44 }}>Legal</Link>
        </div>
      </div>
    </div>
  );
}
